import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Image as ImageIcon, X, Edit, Info } from 'lucide-react';
import { toast } from 'sonner';
import { 
  EstimateImage, 
  fileToDataUrl, 
  compressImage, 
  createThumbnail, 
  getDataUrlSize, 
  formatFileSize,
  MAX_IMAGE_SIZE,
  getTotalStorageUsed,
  isStorageAlmostFull
} from '@/services/imageService';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ImageUploaderProps {
  images: EstimateImage[];
  onAddImages: (newImages: EstimateImage[]) => void;
  onRemoveImage: (imageId: string) => void;
  onUpdateImageDescription: (imageId: string, description: string) => void;
  readOnly?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  images,
  onAddImages,
  onRemoveImage,
  onUpdateImageDescription,
  readOnly = false
}) => {
  const [selectedImage, setSelectedImage] = useState<EstimateImage | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEditDescriptionOpen, setIsEditDescriptionOpen] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [storageUsed, setStorageUsed] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get total storage used on mount
  useEffect(() => {
    const checkStorage = async () => {
      const totalBytes = await getTotalStorageUsed();
      setStorageUsed(formatFileSize(totalBytes));
      
      // Check if storage is almost full
      if (await isStorageAlmostFull()) {
        toast.warning('Image storage is almost full. Consider removing some images.');
      }
    };
    
    checkStorage();
  }, [images]);

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const newImages: EstimateImage[] = [];
    const failedUploads: string[] = [];
    
    // Process each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        failedUploads.push(`${file.name} (not an image)`);
        continue;
      }
      
      // Validate file size
      if (file.size > MAX_IMAGE_SIZE) {
        failedUploads.push(`${file.name} (exceeds 5MB limit)`);
        continue;
      }
      
      try {
        // Convert file to data URL
        const dataUrl = await fileToDataUrl(file);
        
        // Compress the image if it's large
        const compressedDataUrl = file.size > 1024 * 1024 
          ? await compressImage(dataUrl) 
          : dataUrl;
        
        // Create a thumbnail
        const thumbnailDataUrl = await createThumbnail(compressedDataUrl);
        
        // Calculate the size of the compressed image
        const size = getDataUrlSize(compressedDataUrl);
        
        // Create the image object
        const newImage: Omit<EstimateImage, 'id' | 'timestamp'> = {
          dataUrl: compressedDataUrl,
          fileName: file.name,
          description: '',
          size,
        };
        
        newImages.push({
          ...newImage,
          id: `temp-${Date.now()}-${i}`,
          timestamp: Date.now(),
        });
      } catch (error) {
        console.error(`Error processing ${file.name}:`, error);
        failedUploads.push(`${file.name} (processing error)`);
      }
    }
    
    // Report any failed uploads
    if (failedUploads.length > 0) {
      toast.error(`Failed to upload: ${failedUploads.join(', ')}`);
    }
    
    // Add the new images
    if (newImages.length > 0) {
      onAddImages(newImages);
      toast.success(`Added ${newImages.length} image${newImages.length > 1 ? 's' : ''}`);
    }
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle clicking the upload button
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle opening the image preview
  const handlePreviewImage = (image: EstimateImage) => {
    setSelectedImage(image);
    setIsPreviewOpen(true);
  };

  // Handle opening the edit description dialog
  const handleEditDescription = (image: EstimateImage) => {
    setSelectedImage(image);
    setNewDescription(image.description);
    setIsEditDescriptionOpen(true);
  };

  // Handle saving the updated description
  const handleSaveDescription = () => {
    if (selectedImage) {
      onUpdateImageDescription(selectedImage.id, newDescription);
      setIsEditDescriptionOpen(false);
      toast.success('Image description updated');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-medium">Attached Images</h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Images are stored only on your device and are never uploaded to a server.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="text-sm text-muted-foreground">
          Storage used: {storageUsed}
        </div>
      </div>
      
      {!readOnly && (
        <div className="flex items-center space-x-2">
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button 
            onClick={handleUploadClick}
            className="flex items-center space-x-2"
          >
            <ImageIcon className="h-4 w-4" />
            <span>Attach Images</span>
          </Button>
          <p className="text-sm text-muted-foreground">
            Max 5MB per image. Supported formats: JPG, PNG, GIF, WEBP
          </p>
        </div>
      )}
      
      {images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map((image) => (
            <Card key={image.id} className="overflow-hidden">
              <div className="relative h-24 bg-muted">
                <img
                  src={image.dataUrl}
                  alt={image.description || image.fileName}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => handlePreviewImage(image)}
                />
                {!readOnly && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 rounded-full"
                    onClick={() => onRemoveImage(image.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <CardContent className="p-2">
                <div className="flex justify-between items-center">
                  <p className="text-xs truncate" title={image.fileName}>
                    {image.fileName}
                  </p>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleEditDescription(image)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                {image.description && (
                  <p className="text-xs text-muted-foreground truncate" title={image.description}>
                    {image.description}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground border border-dashed rounded-md">
          No images attached
        </div>
      )}
      
      {/* Image Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedImage?.fileName}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center">
            {selectedImage && (
              <>
                <img
                  src={selectedImage.dataUrl}
                  alt={selectedImage.description || selectedImage.fileName}
                  className="max-h-[70vh] max-w-full object-contain"
                />
                {selectedImage.description && (
                  <p className="mt-4 text-center">{selectedImage.description}</p>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Description Dialog */}
      <Dialog open={isEditDescriptionOpen} onOpenChange={setIsEditDescriptionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Image Description</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Add a description for this image..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDescriptionOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDescription}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageUploader;
